import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@clerk/react";
import { api, errMsg, inr } from "@/lib/api";

const STATUSES = [
  ["requested", "New", "bg-parchment-200 text-forest-800 border-parchment-300"],
  ["confirmed", "Confirmed", "bg-sky-100 text-sky-800 border-sky-300"],
  ["paid", "Paid", "bg-amber-100 text-amber-800 border-amber-300"],
  ["delivered", "Delivered", "bg-emerald-100 text-emerald-800 border-emerald-300"],
];

const StatusPicker = ({ order, onStatus }) => {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);
  if (order.method !== "pay_later") return <p className="text-xs text-[#57645A]">Paid online</p>;
  const set = async (status) => {
    if (status === order.status) return;
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${order.id}/status`, { status }, { headers: { Authorization: `Bearer ${await getToken()}` } });
      onStatus(order.id, status);
      toast.success(`Order marked ${STATUSES.find((s) => s[0] === status)[1]}`);
    } catch (e) {
      toast.error(errMsg(e, "Could not update the order"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5 sm:justify-end" data-testid={`order-status-${order.id}`}>
      {STATUSES.map(([key, label, style]) => (
        <button key={key} disabled={busy} onClick={() => set(key)} data-testid={`order-status-${order.id}-${key}`}
          className={`px-2.5 py-1 rounded-full border text-xs font-semibold transition-colors disabled:opacity-60 ${order.status === key ? style : "bg-white text-[#57645A] border-parchment-300 hover:border-forest-700"}`}>
          {label}
        </button>
      ))}
    </div>
  );
};

const fmtDate = (s) => new Date(s).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

const FILTERS = [["all", "All"], ["requested", "New"], ["paid", "Paid"], ["delivered", "Delivered"]];

export default function OrdersList({ orders, onStatus }) {
  const [filter, setFilter] = useState("all");
  if (!orders) return <p className="text-[#57645A]">Loading…</p>;
  if (orders.length === 0) return <div className="bg-white border border-parchment-300 rounded-2xl p-10 text-center text-[#57645A]" data-testid="orders-empty">No store orders yet.</div>;

  const count = (k) => (k === "all" ? orders.length : orders.filter((o) => o.status === k).length);
  const shown = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div className="space-y-3" data-testid="orders-list">
      <div className="flex flex-wrap gap-2 mb-2" data-testid="orders-filter">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`orders-filter-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${filter === k ? "bg-forest-800 text-parchment border-forest-800" : "bg-white text-forest-800 border-parchment-300 hover:border-forest-700"}`}>
            {l} <span className="opacity-70">({count(k)})</span>
          </button>
        ))}
      </div>
      {shown.length === 0 && <div className="bg-white border border-parchment-300 rounded-2xl p-8 text-center text-[#57645A]" data-testid="orders-filter-empty">No orders with this status.</div>}
      {shown.map((o) => (
        <div key={o.id} className="bg-white border border-parchment-300 rounded-2xl p-5 grid sm:grid-cols-[1.1fr_1.3fr_1.2fr] gap-3" data-testid={`order-row-${o.id}`}>
          <div>
            <p className="font-semibold text-forest-800">{o.name}</p>
            <a href={`tel:+91${o.phone}`} className="text-sm text-[#57645A]">{o.phone}</a>
            <p className="text-xs text-[#8A978E] mt-1">{fmtDate(o.created_at)}</p>
          </div>
          <div className="text-sm text-forest-800">
            {o.lines.map((l) => <p key={l.id}>{l.name} × {l.qty}</p>)}
            <p className="text-xs text-[#57645A] mt-1">{o.address}</p>
          </div>
          <div className="sm:text-right space-y-2">
            <p className="font-serif text-2xl font-bold text-forest-800">{inr(o.total)}</p>
            <StatusPicker order={o} onStatus={onStatus} />
          </div>
        </div>
      ))}
    </div>
  );
}
